import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { ArrowRight, Zap, Download, Shield, BarChart3, TrendingUp, ChevronLeft, ChevronRight as ChevronRightIcon, Check, Loader2, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CategoryCard } from "@/components/category-card";
import { ProductCard } from "@/components/product-card";
import { SEO } from "@/components/seo";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import type { Category, Product, CarouselSlide } from "@shared/schema";
import { useState, useEffect, useCallback } from "react";

type SubscriptionPlan = {
  id: number;
  name: string;
  description: string;
  price: string;
  tier: string;
  features: string[] | null;
  products: string[];
  isActive: boolean;
  sortOrder: number;
};

const PLAN_FEATURES: Record<string, string[]> = {
  indicators: [
    "Full Sierra Chart indicator library",
    "VWAP with dynamic bands",
    "Cumulative delta indicator",
    "Session statistics overlay",
    "Market internals dashboard",
    "All future indicator releases",
    "Instant download access",
  ],
  indicators_orderflow: [
    "Everything in the Indicators Plan",
    "Footprint chart templates",
    "DOM heatmap templates",
    "Delta & absorbed volume indicators",
    "Pull rate analysis tools",
    "Stacked imbalance detection",
    "All future order flow releases",
    "Instant download access",
  ],
};

function PricingSection() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "yearly">("yearly");
  const [loadingPlanId, setLoadingPlanId] = useState<number | null>(null);

  const { data: plans, isLoading } = useQuery<SubscriptionPlan[]>({
    queryKey: ["/api/subscription-plans"],
  });

  const activePlans = plans?.filter((p) => p.isActive).sort((a, b) => a.sortOrder - b.sortOrder) ?? [];

  async function handleSubscribe(planId: number) {
    if (!user) {
      window.location.href = `/auth?redirect=/pricing?billing=${billingPeriod}`;
      return;
    }
    setLoadingPlanId(planId);
    try {
      const res = await apiRequest("POST", "/api/subscriptions/create-checkout", {
        planId,
        billingPeriod,
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast({ title: "Error", description: data.message || "Failed to start checkout", variant: "destructive" });
      }
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "Failed to start checkout", variant: "destructive" });
    } finally {
      setLoadingPlanId(null);
    }
  }

  function displayPrice(monthlyPrice: string) {
    const m = Number(monthlyPrice);
    return billingPeriod === "yearly" ? (m * 0.8).toFixed(2) : m.toFixed(2);
  }

  return (
    <section className="py-20 border-t" data-testid="section-pricing">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Zap className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary uppercase tracking-wider">Subscription Plans</span>
          </div>
          <h2 className="text-3xl font-bold mb-4">Get Unlimited Access</h2>
          <p className="text-muted-foreground max-w-xl mx-auto mb-8">
            Subscribe and download everything included in your plan — new tools always included. Cancel anytime.
          </p>

          {/* Billing period toggle */}
          <div className="inline-flex items-center gap-1 p-1 rounded-full border bg-muted/40">
            <button
              onClick={() => setBillingPeriod("monthly")}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                billingPeriod === "monthly"
                  ? "bg-background shadow text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingPeriod("yearly")}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                billingPeriod === "yearly"
                  ? "bg-background shadow text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Yearly
              <span className="bg-green-500 text-white text-xs font-semibold px-1.5 py-0.5 rounded-full leading-none">
                20% off
              </span>
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {[1, 2].map((i) => (
              <Card key={i} className="relative">
                <CardContent className="p-8 space-y-4">
                  <Skeleton className="h-6 w-40" />
                  <Skeleton className="h-10 w-24" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-4/5" />
                  <div className="space-y-2 pt-2">
                    {[1,2,3,4,5].map((j) => <Skeleton key={j} className="h-4 w-3/4" />)}
                  </div>
                  <Skeleton className="h-10 w-full mt-4" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : activePlans.length === 0 ? null : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {activePlans.map((plan, idx) => {
              const isPopular = idx === activePlans.length - 1 && activePlans.length > 1;
              const features = (plan.features && plan.features.length > 0) ? plan.features : (PLAN_FEATURES[plan.tier] ?? []);
              const shownPrice = displayPrice(plan.price);
              const isPlanLoading = loadingPlanId === plan.id;
              return (
                <Card
                  key={plan.id}
                  className={`relative flex flex-col ${isPopular ? "border-primary shadow-lg shadow-primary/10" : ""}`}
                  data-testid={`card-plan-${plan.id}`}
                >
                  {isPopular && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                      <span className="bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full shadow">
                        Most Popular
                      </span>
                    </div>
                  )}
                  <CardContent className="p-8 flex flex-col flex-1">
                    <div className="mb-6">
                      <h3 className="text-lg font-bold mb-1">{plan.name}</h3>
                      <div className="flex items-baseline gap-1 mb-1">
                        <span className="text-4xl font-extrabold">${shownPrice}</span>
                        <span className="text-muted-foreground text-sm">/month</span>
                      </div>
                      {billingPeriod === "yearly" && (
                        <p className="text-xs text-muted-foreground mb-3">
                          <span className="line-through mr-1">${Number(plan.price).toFixed(2)}/mo</span>
                          · billed ${(Number(plan.price) * 12 * 0.8).toFixed(2)}/year
                        </p>
                      )}
                      {plan.products && plan.products.length > 0 && (
                        <Popover>
                          <PopoverTrigger asChild>
                            <button type="button" className="mt-2 mb-2 text-xs text-primary underline underline-offset-2 hover:text-primary/80 flex items-center gap-1">
                              <Package className="w-3 h-3" />
                              {plan.products.length} product{plan.products.length !== 1 ? "s" : ""} included
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-64 p-3">
                            <p className="text-xs font-semibold mb-2 text-foreground">Included in this plan</p>
                            <ul className="space-y-1.5">
                              {plan.products.map((name) => (
                                <li key={name} className="text-xs flex items-center gap-2 text-muted-foreground">
                                  <Package className="w-3 h-3 text-primary flex-shrink-0" />
                                  {name}
                                </li>
                              ))}
                            </ul>
                          </PopoverContent>
                        </Popover>
                      )}
                      <p className="text-sm text-muted-foreground leading-relaxed">{plan.description}</p>
                    </div>

                    <ul className="space-y-2.5 mb-8 flex-1">
                      {features.map((f) => (
                        <li key={f} className="flex items-start gap-2.5 text-sm">
                          <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isPopular ? "text-primary" : "text-green-500"}`} />
                          <span className={f === "Everything in the Indicators Plan" ? "font-medium" : ""}>{f}</span>
                        </li>
                      ))}
                    </ul>

                    <Button
                      className="w-full"
                      variant={isPopular ? "default" : "outline"}
                      data-testid={`button-plan-subscribe-${plan.id}`}
                      onClick={() => handleSubscribe(plan.id)}
                      disabled={!!loadingPlanId}
                    >
                      {isPlanLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Zap className="w-4 h-4 mr-2" />
                      )}
                      Subscribe — ${shownPrice}/mo
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <p className="text-center text-xs text-muted-foreground mt-8">
          {billingPeriod === "yearly"
            ? "Billed annually at 20% off · Cancel anytime · Secure payments via Stripe"
            : "Billed monthly · Cancel anytime · Secure payments via Stripe"}
        </p>
      </div>
    </section>
  );
}

function HeroCarousel() {
  const { data: slides, isLoading } = useQuery<CarouselSlide[]>({ queryKey: ["/api/carousel"] });
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState<Record<number, boolean>>({});

  const displaySlides = slides || [];

  const goToSlide = useCallback((index: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrent(index);
    setTimeout(() => setIsTransitioning(false), 700);
  }, [isTransitioning]);

  const next = useCallback(() => {
    if (displaySlides.length === 0) return;
    goToSlide((current + 1) % displaySlides.length);
  }, [current, goToSlide, displaySlides.length]);

  const prev = useCallback(() => {
    if (displaySlides.length === 0) return;
    goToSlide((current - 1 + displaySlides.length) % displaySlides.length);
  }, [current, goToSlide, displaySlides.length]);

  useEffect(() => {
    if (displaySlides.length === 0) return;
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [next, displaySlides.length]);

  useEffect(() => {
    if (displaySlides.length === 0) return;
    displaySlides.forEach((s, i) => {
      const img = new Image();
      img.onload = () => setImagesLoaded(prev => ({ ...prev, [i]: true }));
      img.src = s.image || "/images/hero-trading.png";
    });
  }, [displaySlides]);

  if (isLoading || displaySlides.length === 0) {
    return (
      <section className="relative overflow-hidden h-[500px] sm:h-[550px] lg:h-[600px] bg-gradient-to-r from-gray-950 via-gray-900 to-gray-800" data-testid="hero-carousel-loading">
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 lg:py-36">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-4">
              <Zap className="w-5 h-5 text-primary" />
              <span className="text-sm font-medium text-primary uppercase tracking-wider">
                Professional Trading Tools
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
              Sierra Chart Templates
              <br />
              <span className="text-primary">& Indicators</span>
            </h1>
            <p className="text-lg text-gray-300 mb-8 leading-relaxed max-w-lg">
              Premium chart templates and custom indicators built for serious traders.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link href="/templates">
                <Button size="lg" data-testid="button-hero-primary-loading">
                  Browse Templates
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link href="/indicators">
                <Button size="lg" variant="outline" className="backdrop-blur-sm bg-white/5" data-testid="button-hero-secondary-loading">
                  View Indicators
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const slide = displaySlides[current];

  return (
    <section className="relative overflow-hidden h-[500px] sm:h-[550px] lg:h-[600px]" data-testid="hero-carousel">
      <div className="absolute inset-0 bg-gradient-to-r from-gray-950 via-gray-900 to-gray-800">
        {displaySlides.map((s, i) => (
          <img
            key={i}
            src={s.image || "/images/hero-trading.png"}
            alt={s.title}
            loading={i === 0 ? "eager" : "lazy"}
            fetchPriority={i === 0 ? "high" : "auto"}
            decoding={i === 0 ? "sync" : "async"}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out ${imagesLoaded[i] ? '' : 'opacity-0'}`}
            style={{ opacity: i === current && imagesLoaded[i] ? 1 : 0 }}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/20" />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 lg:py-36">
        <div className="max-w-2xl">
          <div
            key={current}
            className="animate-in fade-in slide-in-from-bottom-4 duration-500"
          >
            <div className="flex items-center gap-2 mb-4">
              <Zap className="w-5 h-5 text-primary" />
              <span className="text-sm font-medium text-primary uppercase tracking-wider">
                {slide.badge}
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4" data-testid="text-hero-title">
              {slide.title}
              <br />
              <span className="text-primary">{slide.highlight}</span>
            </h1>
            <p className="text-lg text-gray-300 mb-8 leading-relaxed max-w-lg">
              {slide.description}
            </p>
            <div className="flex flex-wrap gap-3">
              <Link href={slide.primaryLink}>
                <Button size="lg" data-testid="button-hero-primary">
                  {slide.primaryLabel}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link href={slide.secondaryLink}>
                <Button size="lg" variant="outline" className="backdrop-blur-sm bg-white/5" data-testid="button-hero-secondary">
                  {slide.secondaryLabel}
                </Button>
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-4">
          <Button
            size="icon"
            variant="ghost"
            onClick={prev}
            className="bg-white/10 backdrop-blur-sm text-white/70"
            aria-label="Previous slide"
            data-testid="button-carousel-prev"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-2">
            {displaySlides.map((_, i) => (
              <button
                key={i}
                onClick={() => goToSlide(i)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  i === current ? "w-8 bg-primary" : "w-2 bg-white/40"
                }`}
                aria-label={`Go to slide ${i + 1}`}
                data-testid={`button-carousel-dot-${i}`}
              />
            ))}
          </div>
          <Button
            size="icon"
            variant="ghost"
            onClick={next}
            className="bg-white/10 backdrop-blur-sm text-white/70"
            aria-label="Next slide"
            data-testid="button-carousel-next"
          >
            <ChevronRightIcon className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const { data: categories, isLoading: categoriesLoading } = useQuery<Category[]>({
    queryKey: ["/api/categories"],
  });

  const { data: featuredProducts, isLoading: productsLoading } = useQuery<Product[]>({
    queryKey: ["/api/products/featured"],
  });

  const templateCategories = categories?.filter((c) => c.group === "templates") || [];
  const indicatorCategories = categories?.filter((c) => c.group === "indicators") || [];

  const homeJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Sierra Chart Pro",
      url: "https://sierrachartpro.com",
      logo: "https://sierrachartpro.com/favicon.png",
      description: "Premium Sierra Chart templates and custom indicators for professional traders.",
      sameAs: [],
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Sierra Chart Pro",
      url: "https://sierrachartpro.com",
      potentialAction: {
        "@type": "SearchAction",
        target: "https://sierrachartpro.com/?q={search_term_string}",
        "query-input": "required name=search_term_string",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Sierra Chart Templates & Indicators",
      description: "Browse our collection of professional Sierra Chart trading templates and custom indicators",
      numberOfItems: featuredProducts?.length || 0,
      itemListElement: featuredProducts?.map((p, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: p.name,
        url: `https://sierrachartpro.com/product/${p.slug}`,
      })) || [],
    },
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="Sierra Chart Templates & Indicators"
        description="Professional Sierra Chart templates & custom indicators — Market Profile, Footprint, Volume Profile, Heatmaps, DOM tools. Instant digital download."
        keywords="buy Sierra Chart templates, Sierra Chart indicators download, market profile TPO template, footprint chart Sierra Chart, volume profile trading, heatmap trading tool, DOM depth of market, orderflow analysis, futures trading tools, day trading indicators, professional chart templates, Sierra Chart setup, trading chart analysis, best Sierra Chart indicators"
        jsonLd={homeJsonLd}
      />
      <HeroCarousel />

      <section className="py-14 border-b bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3">Professional Tools Built for Sierra Chart</h2>
            <p className="text-muted-foreground leading-relaxed">
              Everything we sell is purpose-built for Sierra Chart and ready to import the moment you download. Whether you're analyzing markets, executing trades, or refining your edge, our products give you a professional setup without the hours of manual configuration.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="space-y-2">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center shrink-0">
                  <BarChart3 className="w-4 h-4 text-primary" />
                </div>
                <h3 className="font-semibold text-lg">Sierra Chart Templates</h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Fully configured chart layouts covering Market Profile / TPO, Footprint, Volume Profile, Heatmaps, DOM Ladder, and Orderflow. Each template is pre-built with the right studies, scales, and color schemes so you can drop it into Sierra Chart and trade immediately — no setup required.
              </p>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center shrink-0">
                  <TrendingUp className="w-4 h-4 text-primary" />
                </div>
                <h3 className="font-semibold text-lg">Sierra Chart Indicators</h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Custom Sierra Chart indicators developed by active traders — VWAP bands, cumulative delta, session statistics, market internals, absorbed volume, pull rates, and more. Install in seconds and get institutional-grade market data overlaid directly on your charts.
              </p>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center shrink-0">
                  <Zap className="w-4 h-4 text-primary" />
                </div>
                <h3 className="font-semibold text-lg">Sierra Chart Buy &amp; Sell Signals</h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Precision buy and sell signal studies for Sierra Chart that highlight high-probability entries based on orderflow, delta divergence, and volume imbalance logic. Clear on-chart arrows and alerts mean you never miss a setup — even in fast-moving markets.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 lg:gap-8">
            {[
              {
                icon: Download,
                title: "Instant Download",
                desc: "Get your templates immediately after purchase. Ready to import into Sierra Chart.",
              },
              {
                icon: Shield,
                title: "Professional Quality",
                desc: "Built by experienced traders who use these tools daily in live markets.",
              },
              {
                icon: BarChart3,
                title: "Advanced Analysis",
                desc: "Market Profile, Footprints, DOM, Volume analysis and more in one place.",
              },
            ].map((feature) => (
              <Card key={feature.title} className="text-center">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-md bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {feature.desc}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {templateCategories.length > 0 && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between gap-4 flex-wrap mb-8">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Zap className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-primary uppercase tracking-wider">
                    Templates
                  </span>
                </div>
                <h2 className="text-2xl font-bold" data-testid="text-templates-heading">
                  Chart Templates
                </h2>
              </div>
              <Link href="/templates">
                <Button variant="ghost" data-testid="link-view-all-templates">
                  View All
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
            {categoriesLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => (
                  <Card key={i}>
                    <Skeleton className="w-full aspect-video rounded-t-md" />
                    <CardContent className="p-4">
                      <Skeleton className="h-5 w-3/4 mb-2" />
                      <Skeleton className="h-4 w-full" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {templateCategories.map((category) => (
                  <CategoryCard key={category.id} category={category} />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {indicatorCategories.length > 0 && (
        <section className="py-16 bg-card">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between gap-4 flex-wrap mb-8">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Zap className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-primary uppercase tracking-wider">
                    Indicators
                  </span>
                </div>
                <h2 className="text-2xl font-bold" data-testid="text-indicators-heading">
                  Sierra Chart Indicators
                </h2>
              </div>
              <Link href="/indicators">
                <Button variant="ghost" data-testid="link-view-all-indicators">
                  View All
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {indicatorCategories.map((category) => (
                <CategoryCard key={category.id} category={category} />
              ))}
            </div>
          </div>
        </section>
      )}

      {featuredProducts && featuredProducts.length > 0 && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between gap-4 flex-wrap mb-8">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Zap className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-primary uppercase tracking-wider">
                    Popular
                  </span>
                </div>
                <h2 className="text-2xl font-bold" data-testid="text-featured-heading">
                  Featured Products
                </h2>
              </div>
            </div>
            {productsLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => (
                  <Card key={i}>
                    <Skeleton className="w-full aspect-video rounded-t-md" />
                    <CardContent className="p-4">
                      <Skeleton className="h-5 w-3/4 mb-2" />
                      <Skeleton className="h-4 w-full mb-3" />
                      <Skeleton className="h-9 w-full" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {featuredProducts.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      <section className="py-16 bg-card border-t" data-testid="section-why-us">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary uppercase tracking-wider">Why Us</span>
          </div>
          <h2 className="text-2xl font-bold mb-8">Why Sierra Chart Pro?</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div className="space-y-5 text-muted-foreground leading-relaxed">
              <p>
                Sierra Chart is one of the most powerful and flexible trading platforms available to retail futures traders today. But out of the box, building a professional-grade chart layout from scratch takes hundreds of hours — configuring studies, tuning parameters, designing layouts, and testing everything in live market conditions. Most traders simply don't have that time.
              </p>
              <p>
                Sierra Chart Pro was built by active futures traders who went through exactly that process. Every template and indicator in our store has been refined over years of real trading — not backtested in isolation, but used daily on live accounts in ES, NQ, CL, and other futures markets. When we build a Market Profile template, it reflects how professional traders actually use auction theory. When we package a Footprint chart, every setting has been tuned for maximum readability under the pressure of fast-moving markets.
              </p>
              <p>
                Our products are not generic "starter packs." They are fully configured, ready-to-use professional chart environments that you can import into Sierra Chart in minutes and start trading immediately. We also include written setup instructions with every purchase, so you always know exactly what you're looking at and how to customize it to your specific trading style.
              </p>
            </div>
            <div className="space-y-5 text-muted-foreground leading-relaxed">
              <p>
                What sets Sierra Chart Pro apart is our focus on institutional-grade order flow analysis. Our templates are built around the tools that professional traders and proprietary trading firms use to understand market structure: Volume Profile, Market Profile (TPO), Footprint Charts, Cumulative Delta, DOM Heatmaps, and Liquidity Ladders. These tools reveal the underlying mechanics of price movement — where buyers and sellers are positioned, where liquidity is stacked, and where the market is likely to find support or resistance.
              </p>
              <p>
                Whether you are a day trader scalping the open, a swing trader managing multi-day positions, or an intraday momentum trader following the big institutional flows — our templates give you a clear, organized view of the market that is impossible to build quickly without years of Sierra Chart experience.
              </p>
              <p>
                Every purchase includes instant digital download. You receive the chart template files along with step-by-step installation instructions. If you run into any issues getting set up, our support team is available to help you get your charts running correctly.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 border-t" data-testid="section-who-its-for">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary uppercase tracking-wider">Traders</span>
          </div>
          <h2 className="text-2xl font-bold mb-8">Who Our Tools Are Built For</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "Futures Day Traders",
                body: "Our templates are optimized for the fast-paced environment of intraday futures trading. Whether you trade the ES, NQ, RTY, CL, or GC, our layouts give you a clear picture of intraday volume distribution, key price levels, and order flow context — all on a single screen without clutter.",
              },
              {
                title: "Scalpers & Order Flow Traders",
                body: "Scalping requires precision and speed. Our Footprint chart templates and DOM Heatmap layouts are specifically designed for traders who need to read the tape in real time — identifying absorption, imbalances, exhaustion, and momentum shifts as they happen. Every millisecond counts, and our templates are built to match.",
              },
              {
                title: "Swing & Position Traders",
                body: "For traders managing longer-term positions, our Market Profile and Volume Profile templates provide the structural context you need to identify value areas, high-volume nodes, and directional bias across sessions and weeks. Understand where the market has accepted value and where it has been rejected — and trade accordingly.",
              },
            ].map((item) => (
              <Card key={item.title}>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-3">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.body}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <PricingSection />

      <section className="py-16 bg-card border-t" data-testid="section-how-it-works">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary uppercase tracking-wider">Process</span>
          </div>
          <h2 className="text-2xl font-bold mb-8">How It Works</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {[
              {
                step: "01",
                title: "Browse & Purchase",
                body: "Browse our collection of Sierra Chart templates and custom indicators. Each product page includes a full description, feature list, and screenshots so you know exactly what you're getting before you buy. Complete checkout securely through Stripe — all major credit and debit cards accepted.",
              },
              {
                step: "02",
                title: "Instant Download",
                body: "Immediately after your payment is confirmed, you'll receive access to your download files. Log in to your account and visit My Purchases to download your template or indicator files. You also receive detailed written instructions explaining each component of the template and how to customize it.",
              },
              {
                step: "03",
                title: "Import & Trade",
                body: "Follow the included installation guide to import your template into Sierra Chart. The process takes just a few minutes — copy the files to your Sierra Chart Data folder, open the chart book, and your professional layout is ready to go. If you need help, our support team is here to assist.",
              },
            ].map((item) => (
              <div key={item.step} className="flex gap-5">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-primary font-bold text-sm">{item.step}</span>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.body}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link href="/templates">
              <Button size="lg" data-testid="button-cta-browse">
                Browse All Templates
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
